import { Component, Input, Output, EventEmitter, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IconComponent } from '../../icons/icon.component';
import { ExotelApiService, MakeCallRequest, ExotelCallStatusPayload } from '../../../core/services/exotel/exotel-api.service';
import { AuthService } from '../../../core/services/auth/auth.service';
import { PatientsApiService } from '../../../core/services/patients/patients-api.service';

/**
 * Global Call Modal Component
 * Handles click-to-call via Exotel with call progress timer, real-time status polling, and direct dial fallback.
 *
 * @example
 * <app-call-modal
 *   [isOpen]="showCallModal"
 *   [patient]="selectedPatient"
 *   (closed)="closeCallModal()"
 *   (callEnded)="onCallEnded()"
 * ></app-call-modal>
 */
@Component({
  selector: 'app-call-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponent],
  templateUrl: './call-modal.component.html',
  styleUrl: './call-modal.component.css',
})
export class CallModalComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() patient: any = null;
  @Input() patientName = '';
  @Input() phoneNumber = '';
  @Input() patientId = '';

  @Output() closed = new EventEmitter<void>();
  @Output() callStarted = new EventEmitter<void>();
  @Output() callEnded = new EventEmitter<{ duration: number; remarks?: string }>();

  isCallInProgress = false;
  callCompleted = false; // true after a clean 'completed' status — shows completion screen
  callTimer = 0;
  callRemarks = '';
  isSavingTimeline = false;
  private callTimerInterval: any = null;
  private callStatusInterval: any = null;
  private callSid: string | null = null;
  private completedDuration = 0; // stored so callEnded fires when user closes, not when status arrives
  callError = '';
  callStatus = '';

  private readonly exotelConfig = {
    defaultCallerId: '95-138-86363',
  };

  constructor(
    private authService: AuthService,
    private exotelService: ExotelApiService,
    private patientsService: PatientsApiService
  ) {}

  get resolvedPatientName(): string {
    return this.patientName || this.patient?.name || 'Patient';
  }

  get resolvedPhoneNumber(): string {
    const raw = this.phoneNumber || this.patient?.contact_number || this.patient?.phoneNumber || this.patient?.phone;
    return raw && raw !== 'N/A' ? raw : '';
  }

  get resolvedPatientId(): string {
    return this.patientId || this.patient?._id || this.patient?.id || this.patient?.patient_id || '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && changes['isOpen'].currentValue) {
      this.callError = '';
      this.isCallInProgress = false;
      this.callCompleted = false;
      this.completedDuration = 0;
      this.callTimer = 0;
      this.callRemarks = '';
      this.isSavingTimeline = false;
      this.callStatus = '';
      this.callSid = null;
      this.stopCallTimer();
      this.stopStatusPolling();
    }
  }

  ngOnDestroy(): void {
    this.stopCallTimer();
    this.stopStatusPolling();
  }

  close(): void {
    if (this.isCallInProgress) return;
    if (this.callCompleted) {
      this.saveTimelineAndClose(true);
      return;
    }
    this.stopCallTimer();
    this.stopStatusPolling();
    this.callCompleted = false;
    this.completedDuration = 0;
    this.callRemarks = '';
    this.closed.emit();
  }

  saveTimelineAndClose(skipRemarks: boolean = false): void {
    if (this.isCallInProgress) return;
    this.stopCallTimer();
    this.stopStatusPolling();

    const duration = this.completedDuration || this.callTimer;
    const durationStr = this.formatCallTimer();
    const pid = this.resolvedPatientId;
    const remarksText = (!skipRemarks && this.callRemarks && this.callRemarks.trim()) ? this.callRemarks.trim() : '';

    if (pid && !this.isSavingTimeline) {
      this.isSavingTimeline = true;
      const info = remarksText
        ? `[Outgoing Call] (${durationStr}): ${remarksText}`
        : `[Outgoing Call] Completed (${durationStr})`;

      this.patientsService.addTimelineEvent(pid, {
        date: new Date().toISOString(),
        information: info
      }).subscribe({
        next: () => {
          this.isSavingTimeline = false;
          this.finalizeClose(duration, remarksText);
        },
        error: (err) => {
          console.error('Error saving call to timeline:', err);
          this.isSavingTimeline = false;
          this.finalizeClose(duration, remarksText);
        }
      });
    } else {
      this.finalizeClose(duration, remarksText);
    }
  }

  private finalizeClose(duration: number, remarks?: string): void {
    this.callEnded.emit({ duration, remarks });
    this.callCompleted = false;
    this.completedDuration = 0;
    this.callRemarks = '';
    this.closed.emit();
  }

  initiateCall(): void {
    if (this.isCallInProgress) return;

    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      this.callError = 'User not authenticated.';
      return;
    }

    if (!currentUser.contact_number) {
      this.callError = 'Your contact number is not available in user profile.';
      return;
    }

    const toPhone = this.resolvedPhoneNumber;
    if (!toPhone) {
      this.callError = 'Patient contact number is not available.';
      return;
    }

    const callData: MakeCallRequest = {
      to: toPhone,
      from_number: currentUser.contact_number,
      caller_id: this.exotelConfig.defaultCallerId,
      patient_id: this.resolvedPatientId || undefined,
    };

    this.isCallInProgress = true;
    this.callError = '';
    this.callStatus = 'Initiating...';
    this.callTimer = 0;
    this.stopCallTimer();
    this.callStarted.emit();

    this.exotelService.makeCall(callData).subscribe({
      next: (response: any) => {
        if (response.success || response.Call || response.call_sid || response.Sid) {
          const sid = response.Call?.Sid || response.call_sid || response.Sid || response.CallSid;
          if (sid) {
            this.callSid = sid;
            this.startStatusPolling(sid);
          }
          this.callStatus = 'Ringing...';
        } else {
          this.callError = this.extractErrorMessage(response);
          this.isCallInProgress = false;
          this.stopCallTimer();
          this.stopStatusPolling();
        }
      },
      error: (error: any) => {
        console.error('Error initiating call:', error);
        this.callError = this.extractErrorMessage(error);
        this.isCallInProgress = false;
        this.stopCallTimer();
        this.stopStatusPolling();
      },
    });
  }

  endCall(): void {
    const elapsedDuration = this.callTimer;
    this.isCallInProgress = false;
    this.stopCallTimer();
    this.stopStatusPolling();
    this.callEnded.emit({ duration: elapsedDuration });
    this.closed.emit();
  }

  private startCallTimer(): void {
    if (this.callTimerInterval) return;
    this.callTimer = 0;
    this.callTimerInterval = setInterval(() => {
      this.callTimer++;
    }, 1000);
  }

  private stopCallTimer(): void {
    if (this.callTimerInterval) {
      clearInterval(this.callTimerInterval);
      this.callTimerInterval = null;
    }
  }

  private startStatusPolling(callSid: string): void {
    this.stopStatusPolling();

    const checkStatus = () => {
      if (!this.isCallInProgress || !this.callSid) {
        this.stopStatusPolling();
        return;
      }

      this.exotelService.getCallDetails(callSid).subscribe({
        next: (statusResponse: any) => {
          const callObj = statusResponse?.Call || statusResponse?.call || statusResponse;
          const rawStatus = callObj?.Status || 
                            callObj?.status || 
                            statusResponse?.CallStatus ||
                            statusResponse?.Status ||
                            statusResponse?.status;

          if (rawStatus) {
            this.callStatus = rawStatus;
            const normalized = rawStatus.toLowerCase();

            // When call connects ('in-progress'), start live conversation timer
            if (normalized === 'in-progress') {
              if (!this.callTimerInterval) {
                this.startCallTimer();
              }
            }

            // Terminal call states
            if (['completed', 'busy', 'failed', 'no-answer', 'canceled', 'cancelled'].includes(normalized)) {
              this.isCallInProgress = false;
              this.stopCallTimer();
              this.stopStatusPolling();

              // Use Exotel duration if non-null/non-zero, otherwise keep frontend timer
              const reportedDuration = callObj?.Duration ?? callObj?.ConversationDuration ?? statusResponse?.Duration;
              const numericDuration = Number(reportedDuration);
              const finalDuration = (!isNaN(numericDuration) && numericDuration > 0)
                ? numericDuration
                : this.callTimer;

              this.callTimer = finalDuration;

              if (normalized === 'completed') {
                // Show completion screen — callEnded fires when the user clicks Close
                this.completedDuration = finalDuration;
                this.callCompleted = true;
              } else {
                // Non-completed terminals: fire callEnded now, show error, then auto-close
                this.callEnded.emit({ duration: finalDuration });
                this.callError = this.terminalStatusLabel(rawStatus);
                setTimeout(() => {
                  if (!this.isCallInProgress) this.closed.emit();
                }, 2500);
              }
            }
          }
        },
        error: (err: any) => {
          console.error('Error polling call details:', err);
        }
      });
    };

    // Immediate check, then poll every 2.5 seconds
    checkStatus();
    this.callStatusInterval = setInterval(checkStatus, 2500);
  }

  private stopStatusPolling(): void {
    if (this.callStatusInterval) {
      clearInterval(this.callStatusInterval);
      this.callStatusInterval = null;
    }
  }

  private terminalStatusLabel(status: string): string {
    const map: Record<string, string> = {
      busy: 'Line was busy.',
      'no-answer': 'Call was not answered.',
      failed: 'Call failed to connect.',
      canceled: 'Call was cancelled.',
      cancelled: 'Call was cancelled.',
    };
    return map[status.toLowerCase()] ?? `Call ended with status: ${status}`;
  }

  formatCallTimer(): string {
    const minutes = Math.floor(this.callTimer / 60);
    const seconds = this.callTimer % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  private extractErrorMessage(err: any): string {
    if (!err) return 'Failed to initiate call. Please try again.';

    if (typeof err === 'string') {
      try {
        const parsed = JSON.parse(err);
        return this.extractErrorMessage(parsed);
      } catch {
        return err;
      }
    }

    if (err.RestException?.Message) {
      return err.RestException.Message;
    }

    if (err.error) {
      if (typeof err.error === 'string') {
        try {
          const parsed = JSON.parse(err.error);
          return this.extractErrorMessage(parsed);
        } catch {
          return err.error;
        }
      }
      if (err.error.RestException?.Message) {
        return err.error.RestException.Message;
      }
      if (err.error.message) {
        return err.error.message;
      }
      if (err.error.error) {
        return typeof err.error.error === 'string' ? err.error.error : this.extractErrorMessage(err.error.error);
      }
    }

    if (err.message) return err.message;
    if (err.error && typeof err.error === 'string') return err.error;

    return 'Failed to initiate call. Please try again.';
  }
}
